/**
 * MapControls - Search, Filter, and Discovery Command Center
 * 
 * Search bar + filter panel integrated with the cosmic map.
 * 
 * Filter panel sections:
 * 1. Active filter pills (always visible above map when filters are on)
 * 2. Quick Filters: My Stars, My Projects, Has Challenges, Has Forks, My Classes
 * 3. Project Type: Physical / Digital / Hybrid
 * 4. Zones: 8-zone grid with project counts
 * 5. Sort: Newest, Most Forked, Most Viewed, Most Starred
 * 6. Show/hide Challenges toggle
 * 7. Show Use echoes toggle (Oct 2026) — ghost copies of projects in the
 *    other zones they are used in. Off by default; echoes never appear
 *    on the map unless this is on.
 * 
 * Collections have been consolidated into the SelectionTray (bottom workspace).
 * They no longer appear in this filter panel.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Star,   // ZoomIn/ZoomOut/RotateCcw/HelpCircle moved to MapChrome
  Grid, Map as MapIcon,  X, SlidersHorizontal,
  User, Target, Clock, GitFork, Eye, ChevronDown, ChevronRight,
  Package, Monitor, Layers, Check, ArrowUpRight, GraduationCap, Scan,
} from 'lucide-react';
import { cn } from '../utils/cn';
import MapChrome from '../components/map/MapChrome';
import { ZONES, getProjectZone, PROJECT_TYPES } from '../config/zones';
import { Button } from '../components/ui';

// ============================================================================
// CONFIG
// ============================================================================

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest First', icon: Clock },
  { id: 'most_forked', label: 'Most Forked', icon: GitFork },
  { id: 'most_viewed', label: 'Most Viewed', icon: Eye },
  { id: 'most_starred', label: 'Most Starred', icon: Star },
];

const QUICK_FILTERS = [
  { id: 'starred', label: 'My Stars', icon: Star, color: 'amber' },
  { id: 'mine', label: 'My Projects', icon: User, color: 'purple' },
  { id: 'challenges', label: 'Has Challenges', icon: Target, color: 'orange' },
  { id: 'has_forks', label: 'Has Forks', icon: GitFork, color: 'emerald' },
  { id: 'my_cohorts', label: 'My Classes', icon: GraduationCap, color: 'sky' },
];

const TYPE_FILTERS = [
  { id: 'physical', label: 'Physical', icon: Package, color: PROJECT_TYPES.physical.color },
  { id: 'digital', label: 'Digital', icon: Monitor, color: PROJECT_TYPES.digital.color },
  { id: 'hybrid', label: 'Hybrid', icon: Layers, color: PROJECT_TYPES.hybrid.color },
];

// ============================================================================
// SHAPE FILTERS — sensory vocabulary from Laban Movement Analysis
// Users select 1-2 when creating. Displayed as tappable symbols in filter panel.
// ============================================================================
const SHAPE_FILTERS = [
  { id: 'round',         symbol: '●', label: 'Round' },
  { id: 'wide',          symbol: '▬', label: 'Wide' },
  { id: 'narrow',        symbol: '▲', label: 'Narrow' },
  { id: 'twisted',       symbol: '∿', label: 'Twisted' },
  { id: 'symmetrical',   symbol: '⬡', label: 'Symmetrical' },
  { id: 'asymmetrical',  symbol: '⬠', label: 'Asymmetrical' },
  { id: 'rising',        symbol: '↑', label: 'Rising' },
  { id: 'sinking',       symbol: '↓', label: 'Sinking' },
  { id: 'expanding',     symbol: '◎', label: 'Expanding' },
  { id: 'contracting',   symbol: '⊙', label: 'Contracting' },
];

// ============================================================================
// ACTIVE FILTER PILLS (shown above map, outside filter panel)
// ============================================================================

const ActiveFilterPills = ({
  activeZones, activeQuickFilters, activeTypeFilters, activeShapeFilters = [],
  sortBy, onRemoveZone, onRemoveQuickFilter, onRemoveTypeFilter,
  onRemoveShapeFilter, onResetSort, onClearAll, filteredCount, totalCount
}) => {
  const pills = [];

  activeQuickFilters.forEach(fId => {
    const f = QUICK_FILTERS.find(q => q.id === fId);
    if (f) pills.push({ key: `q-${fId}`, label: f.label, icon: f.icon, color: f.color, onRemove: () => onRemoveQuickFilter(fId) });
  });
  activeTypeFilters.forEach(tId => {
    const t = TYPE_FILTERS.find(tf => tf.id === tId);
    if (t) pills.push({ key: `t-${tId}`, label: t.label, icon: t.icon, color: 'purple', onRemove: () => onRemoveTypeFilter(tId) });
  });
  activeShapeFilters.forEach(sId => {
    const s = SHAPE_FILTERS.find(sf => sf.id === sId);
    if (s) pills.push({ key: `s-${sId}`, label: `${s.symbol} ${s.label}`, color: 'violet', onRemove: () => onRemoveShapeFilter(sId) });
  });
  activeZones.forEach(zId => {
    const z = ZONES[zId];
    if (z) pills.push({ key: `z-${zId}`, label: z.shortLabel, dotColor: z.color, onRemove: () => onRemoveZone(zId) });
  });
  if (sortBy !== 'newest') {
    const s = SORT_OPTIONS.find(so => so.id === sortBy);
    if (s) pills.push({ key: 'sort', label: s.label, icon: s.icon, onRemove: onResetSort });
  }

  if (pills.length === 0) return null;

  return (
    <div className="absolute top-[108px] left-4 right-4 z-10 pointer-events-none">
      <div className="flex items-center gap-1.5 flex-wrap pointer-events-auto">
        {/* Count badge */}
        <span className="text-[11px] text-zinc-500 font-medium mr-1 flex-shrink-0">
          {filteredCount}/{totalCount}
        </span>

        {pills.map(pill => (
          <button
            key={pill.key}
            onClick={pill.onRemove}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border',
              'transition-all hover:opacity-75 active:scale-95',
              pill.color === 'violet'
                ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                : 'bg-zinc-900/90 backdrop-blur-sm border-zinc-700/60 text-zinc-300'
            )}
          >
            {pill.dotColor && (
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: pill.dotColor }} />
            )}
            {pill.icon && !pill.dotColor && <pill.icon className="w-3 h-3" />}
            {pill.label}
            <X className="w-3 h-3 ml-0.5 text-zinc-500" />
          </button>
        ))}

        {pills.length > 1 && (
          <button
            onClick={onClearAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors ml-1"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MAP CONTROLS
// ============================================================================

const MapControls = ({
  // Search
  searchQuery,
  onSearchChange,
  allProjects = [],
  allChallenges = [],
  // Zone filters
  activeZones,
  onToggleZone,
  // Challenges visibility
  showChallenges,
  onToggleChallenges,
  // Use echoes visibility (opt-in)
  showEchoes = false,
  onToggleEchoes,
  echoCount = 0,
  // Quick filters
  activeQuickFilters = [],
  onToggleQuickFilter,
  // Type filters
  activeTypeFilters = [],
  onToggleTypeFilter,
  // Shape filters
  activeShapeFilters = [],
  onToggleShapeFilter,
  // Sort
  sortBy = 'newest',
  onSortChange,
  // Starring
  starredIds = [],
  onStarProject,
  onUnstarProject,
  // Navigation
  onFlyToProject,
  onSelectProject,
  onFlyToChallenge,
  onSelectChallenge,
  // Map controls
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
  viewMode,
  onViewModeChange,
  onCreateProject,
  isMultiSelectMode,
  isTrayExpanded,
  onToggleLegend,
  showLegend,
  // Lens
  isLensActive,
  lensProbeProject,
  onActivateLens,
  onDeactivateLens,
  // User
  currentUserId,
  // Stats
  filteredProjectCount,
  totalProjectCount,
}) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const sortRef = useRef(null);
  const filterRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
      if (filterRef.current && !filterRef.current.contains(e.target) &&
          !e.target.closest('[data-filter-toggle]')) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Open results on typing
  useEffect(() => {
    if (searchQuery.trim().length >= 1) {
      setShowSearchResults(true);
      setActiveResultIndex(-1);
    }
  }, [searchQuery]);

  // ---- Search scoring ----
  const searchResults = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();
    if (query.length < 1) return [];
    return allProjects
      .map(p => {
        const name = (p.name || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        const tagList = (p.tags || []).map(t => typeof t === 'string' ? t : t.name || '');
        const tagsJoined = tagList.join(' ').toLowerCase();
        const author = (p.author?.username || '').toLowerCase();
        let score = 0;
        let matchType = null;
        let matchedTag = null;
        let descSnippet = null;
        
        if (name === query) { score = 100; matchType = 'name'; }
        else if (name.startsWith(query)) { score = 80; matchType = 'name'; }
        else if (name.includes(query)) { score = 60; matchType = 'name'; }
        else if (author.includes(query)) { score = 40; matchType = 'author'; }
        else if (tagsJoined.includes(query)) {
          score = 30; matchType = 'tag';
          matchedTag = tagList.find(t => t.toLowerCase().includes(query));
        }
        else if (desc.includes(query)) {
          score = 10; matchType = 'description';
          const idx = desc.indexOf(query);
          const start = Math.max(0, idx - 30);
          const end = Math.min(desc.length, idx + query.length + 50);
          descSnippet = (start > 0 ? '\u2026' : '') + (p.description || '').slice(start, end) + (end < desc.length ? '\u2026' : '');
        }
        return { project: p, score, matchType, matchedTag, descSnippet };
      })
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [searchQuery, allProjects]);

  const challengeResults = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();
    if (query.length < 1) return [];
    return allChallenges
      .map(c => {
        const title = (c.challenge_title || c.challengeTitle || c.title || '').toLowerCase();
        const desc = (c.challenge_description || c.challengeDescription || c.description || '').toLowerCase();
        const type = (c.challenge_type || c.challengeType || '').toLowerCase();
        const skills = (c.skills_needed || c.skillsNeeded || []).join(' ').toLowerCase();
        let score = 0;
        if (title === query) score = 100;
        else if (title.startsWith(query)) score = 80;
        else if (title.includes(query)) score = 60;
        else if (type.includes(query)) score = 40;
        else if (skills.includes(query)) score = 35;
        else if (desc.includes(query)) score = 10;
        return { challenge: c, score };
      })
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(s => s.challenge);
  }, [searchQuery, allChallenges]);

  // Flat results for keyboard nav
  const allResults = useMemo(() => {
    const items = [];
    searchResults.forEach(r => items.push({ type: 'project', data: r.project, match: r }));
    challengeResults.forEach(c => items.push({ type: 'challenge', data: c }));
    return items;
  }, [searchResults, challengeResults]);

  const hasAnyResults = allResults.length > 0;

  // Zone for display
  const getZone = useCallback((project) => {
    try { return getProjectZone(project); } catch { return null; }
  }, []);

  // Project counts per zone
  const zoneProjectCounts = useMemo(() => {
    const counts = {};
    (allProjects || []).forEach(p => {
      const zone = getZone(p);
      if (zone) counts[zone.id] = (counts[zone.id] || 0) + 1;
    });
    return counts;
  }, [allProjects, getZone]);

  // ---- Handlers ----
  const handleResultClick = useCallback((project) => {
    onFlyToProject?.(project);
    onSelectProject?.(project);
    setShowSearchResults(false);
  }, [onFlyToProject, onSelectProject]);

  const handleChallengeResultClick = useCallback((challenge) => {
    onFlyToChallenge?.(challenge);
    onSelectChallenge?.(challenge);
    setShowSearchResults(false);
  }, [onFlyToChallenge, onSelectChallenge]);

  const handleStarClick = useCallback((e, project) => {
    e.stopPropagation();
    const isStarred = starredIds.includes(project.id);
    if (isStarred) onUnstarProject?.(project.id);
    else onStarProject?.(project.id);
  }, [starredIds, onStarProject, onUnstarProject]);

  const handleSearchClear = useCallback(() => {
    onSearchChange('');
    setShowSearchResults(false);
    inputRef.current?.focus();
  }, [onSearchChange]);

  // Keyboard nav
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowSearchResults(false);
      e.target.blur();
      return;
    }
    if (!showSearchResults || !hasAnyResults) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveResultIndex(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveResultIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && activeResultIndex >= 0) {
      e.preventDefault();
      const item = allResults[activeResultIndex];
      if (item.type === 'project') handleResultClick(item.data);
      else handleChallengeResultClick(item.data);
    }
  }, [showSearchResults, hasAnyResults, allResults, activeResultIndex, handleResultClick, handleChallengeResultClick]);

  const activeFilterCount = activeZones.length + activeQuickFilters.length + activeTypeFilters.length
    + activeShapeFilters.length + (sortBy !== 'newest' ? 1 : 0);

  const handleClearAllFilters = useCallback(() => {
    activeZones.forEach(z => onToggleZone(z));
    activeQuickFilters.forEach(f => onToggleQuickFilter(f));
    activeTypeFilters.forEach(t => onToggleTypeFilter?.(t));
    activeShapeFilters.forEach(s => onToggleShapeFilter?.(s));
    if (sortBy !== 'newest') onSortChange?.('newest');
  }, [activeZones, activeQuickFilters, activeTypeFilters, activeShapeFilters, sortBy,
      onToggleZone, onToggleQuickFilter, onToggleTypeFilter, onToggleShapeFilter, onSortChange]);

  return (
    <>
      {/* ====== ACTIVE FILTER PILLS (above map, always visible) ====== */}
      <ActiveFilterPills
        activeZones={activeZones}
        activeQuickFilters={activeQuickFilters}
        activeTypeFilters={activeTypeFilters}
        activeShapeFilters={activeShapeFilters}
        sortBy={sortBy}
        onRemoveZone={onToggleZone}
        onRemoveQuickFilter={onToggleQuickFilter}
        onRemoveTypeFilter={onToggleTypeFilter}
        onRemoveShapeFilter={onToggleShapeFilter}
        onResetSort={() => onSortChange?.('newest')}
        onClearAll={handleClearAllFilters}
        filteredCount={filteredProjectCount ?? allProjects.length}
        totalCount={totalProjectCount ?? allProjects.length}
      />

      {/* ====== TOP BAR  --  Search + Filter Toggle ====== */}
      <div className="absolute top-16 left-4 right-4 z-20 flex items-center gap-2 pointer-events-none">

        {/* Search input */}
        <div className="relative flex-1 max-w-md pointer-events-auto" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => { if (searchQuery.trim()) setShowSearchResults(true); }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search projects, tags, people..."
            className={cn(
              'w-full bg-zinc-900/90 backdrop-blur-xl border rounded-xl pl-10 pr-10 py-2.5',
              'text-sm text-white placeholder:text-zinc-500',
              'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
              'transition-all',
              showSearchResults && hasAnyResults ? 'border-zinc-700 rounded-b-none' : 'border-zinc-800'
            )}
          />
          {searchQuery && (
            <button onClick={handleSearchClear} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-700 transition-colors">
              <X className="w-3.5 h-3.5 text-zinc-500" />
            </button>
          )}

          {/* Search results dropdown */}
          {showSearchResults && searchQuery.trim() && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 bg-zinc-900/95 backdrop-blur-xl border border-t-0 border-zinc-700 rounded-b-xl shadow-2xl overflow-hidden z-30"
              style={{ animation: 'dropdownIn 0.15s ease-out' }}
            >
              {hasAnyResults ? (
                <div className="max-h-96 overflow-y-auto">
                  {/* Projects */}
                  {searchResults.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1">
                        <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Projects</span>
                      </div>
                      {searchResults.map((result, idx) => {
                        const project = result.project;
                        const zone = getZone(project);
                        const isStarred = starredIds.includes(project.id);
                        const flatIdx = idx;
                        const tagList = (project.tags || []).slice(0, 3).map(t => typeof t === 'string' ? t : t.name || '');
                        
                        return (
                          <div
                            key={`p-${project.id}`}
                            onClick={() => handleResultClick(project)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors group',
                              activeResultIndex === flatIdx ? 'bg-purple-500/10' : 'hover:bg-zinc-800/60'
                            )}
                          >
                            <div className="w-10 h-10 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0 border border-zinc-700/50">
                              {(project.image_url || project.imageUrl) ? (
                                <img src={project.image_url || project.imageUrl} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs font-semibold"
                                  style={zone ? { background: `${zone.color}15` } : {}}>
                                  {project.name?.[0]?.toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Row 1: name + type */}
                              <div className="flex items-center gap-1.5">
                                {zone && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />}
                                <span className="text-sm font-medium text-white truncate">{project.name}</span>
                                {(() => {
                                  const pt = project.project_type || project.projectType || 'digital';
                                  const cfg = PROJECT_TYPES[pt];
                                  return cfg ? (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium capitalize flex-shrink-0"
                                      style={{ backgroundColor: cfg.color + '22', color: cfg.color }}>
                                      {pt}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              
                              {/* Row 2: author + stats */}
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={cn('text-[11px] truncate', result.matchType === 'author' ? 'text-purple-400 font-medium' : 'text-zinc-500')}>
                                  {result.matchType === 'author' ? `@${project.author?.username}` : (project.author?.username || 'Unknown')}
                                </span>
                                {(project.fork_count > 0 || project.star_count > 0) && (
                                  <span className="text-[11px] text-zinc-600 flex items-center gap-1.5">
                                    {project.fork_count > 0 && <span className="flex items-center gap-0.5"><GitFork className="w-2.5 h-2.5" />{project.fork_count}</span>}
                                    {project.star_count > 0 && <span className="flex items-center gap-0.5"><Star className="w-2.5 h-2.5" />{project.star_count}</span>}
                                  </span>
                                )}
                              </div>
                              
                              {/* Row 3: match context — what actually matched */}
                              {result.matchType === 'tag' && result.matchedTag && (
                                <div className="mt-1">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400 border border-purple-500/20 font-medium">
                                    #{result.matchedTag}
                                  </span>
                                </div>
                              )}
                              {result.matchType === 'description' && result.descSnippet && (
                                <p className="text-[10px] text-zinc-500 mt-1 truncate italic">
                                  {result.descSnippet}
                                </p>
                              )}
                              
                              {/* Row 4: tags (if matched by name/author, show tags as bonus context) */}
                              {result.matchType !== 'tag' && tagList.length > 0 && (
                                <div className="flex items-center gap-1 mt-1">
                                  {tagList.map((tag, i) => (
                                    <span key={i} className="text-[9px] px-1 py-0 rounded bg-zinc-800/80 text-zinc-500">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => handleStarClick(e, project)}
                              className={cn(
                                'p-1.5 rounded-lg transition-all flex-shrink-0',
                                'opacity-0 group-hover:opacity-100',
                                isStarred ? 'text-amber-400 opacity-100 hover:bg-amber-500/10' : 'text-zinc-600 hover:text-amber-400 hover:bg-zinc-700'
                              )}
                            >
                              <Star className={cn('w-4 h-4', isStarred && 'fill-amber-400')} />
                            </button>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Challenges */}
                  {challengeResults.length > 0 && (
                    <>
                      {searchResults.length > 0 && <div className="h-px bg-zinc-800 mx-3" />}
                      <div className="px-3 pt-2 pb-1">
                        <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Challenges</span>
                      </div>
                      {challengeResults.map((challenge, idx) => {
                        const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || 'Untitled';
                        const type = challenge.challenge_type || challenge.challengeType || '';
                        const responseCount = challenge.response_count || challenge.responseCount || 0;
                        const author = challenge.requester?.username || challenge.author?.username || '';
                        const flatIdx = searchResults.length + idx;
                        return (
                          <div
                            key={`c-${challenge.id}`}
                            onClick={() => handleChallengeResultClick(challenge)}
                            className={cn(
                              'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors group',
                              activeResultIndex === flatIdx ? 'bg-purple-500/10' : 'hover:bg-zinc-800/60'
                            )}
                          >
                            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                              <Target className="w-4 h-4 text-amber-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-white truncate block">{title}</span>
                              <div className="flex items-center gap-2 mt-0.5">
                                {type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-medium">{type.replace(/_/g, ' ')}</span>}
                                {author && <span className="text-[11px] text-zinc-500 truncate">{author}</span>}
                                {responseCount > 0 && <span className="text-[11px] text-zinc-600">{responseCount} response{responseCount !== 1 ? 's' : ''}</span>}
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-zinc-600 -rotate-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm text-zinc-500">No results for &ldquo;{searchQuery}&rdquo;</p>
                </div>
              )}

              {/* Footer */}
              {hasAnyResults && (
                <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-900/80">
                  <p className="text-[11px] text-zinc-600">
                    {searchResults.length} project{searchResults.length !== 1 ? 's' : ''}
                    {challengeResults.length > 0 && <> &middot; {challengeResults.length} challenge{challengeResults.length !== 1 ? 's' : ''}</>}
                    {' '}&middot; <span className="text-zinc-500">&uarr;&darr;</span> navigate &middot; <span className="text-zinc-500">Enter</span> select
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter toggle button — glows fuchsia when lens is active */}
        <button
          data-filter-toggle
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'flex items-center gap-2 px-3.5 py-2.5 bg-zinc-900/90 backdrop-blur-xl border rounded-xl transition-all pointer-events-auto',
            isLensActive
              ? 'border-fuchsia-500 text-fuchsia-400'
              : showFilters
                ? 'border-purple-500 text-purple-400'
                : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">Filters</span>
          {(activeFilterCount > 0 || isLensActive) && (
            <span className={cn(
              'w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center',
              isLensActive ? 'bg-fuchsia-500' : 'bg-purple-500'
            )}>
              {activeFilterCount + (isLensActive ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* ====== FILTER PANEL ====== */}
      {showFilters && (
        <div
          ref={filterRef}
          className="absolute top-20 right-4 z-30 w-80 bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl shadow-2xl overflow-hidden pointer-events-auto max-h-[calc(100vh-120px)] overflow-y-auto"
          style={{ animation: 'dropdownIn 0.15s ease-out' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2 sticky top-0 bg-zinc-900/95 backdrop-blur-xl z-10">
            <h3 className="text-sm font-semibold text-white">Filters & Sort</h3>
            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <button onClick={handleClearAllFilters} className="text-[11px] text-purple-400 hover:text-purple-300 transition-colors">
                  Clear all
                </button>
              )}
              <button onClick={() => setShowFilters(false)} className="p-1 hover:bg-zinc-800 rounded-lg transition-colors">
                <X className="w-3.5 h-3.5 text-zinc-500" />
              </button>
            </div>
          </div>

          {/* ---- Lens — Discover Hidden Connections ---- */}
          <div className="px-4 pb-3">
            <button
              onClick={() => {
                if (isLensActive) {
                  onDeactivateLens?.();
                } else {
                  onActivateLens?.();
                }
              }}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all',
                isLensActive
                  ? 'bg-fuchsia-500/15 border-fuchsia-500/60 text-fuchsia-300'
                  : 'bg-zinc-800/30 border-zinc-700/40 text-zinc-400 hover:border-fuchsia-500/40 hover:text-fuchsia-300 hover:bg-fuchsia-500/5'
              )}
            >
              <div className={cn(
                'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                isLensActive ? 'bg-fuchsia-500/25' : 'bg-zinc-800'
              )}>
                <Scan className="w-5 h-5" />
              </div>
              <div className="flex-1 text-left">
                <span className="text-sm font-semibold block">
                  {isLensActive ? 'Lens Active' : 'Lens'}
                </span>
                <span className={cn('text-[11px] block leading-tight', isLensActive ? 'text-fuchsia-400/70' : 'text-zinc-600')}>
                  {isLensActive
                    ? `Showing connections for "${lensProbeProject?.name || 'selected project'}"`
                    : 'Select a project, then tap to reveal hidden connections'
                  }
                </span>
              </div>
              {isLensActive && (
                <X className="w-4 h-4 text-fuchsia-400/60 flex-shrink-0" />
              )}
            </button>
          </div>

          <div className="h-px bg-zinc-800 mx-4" />

          {/* ---- Quick Filters ---- */}
          <div className="px-4 pb-3">
            <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Quick Filters</label>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_FILTERS.map(filter => {
                const isActive = activeQuickFilters.includes(filter.id);
                const Icon = filter.icon;
                const colorMap = {
                  amber: 'text-amber-400 bg-amber-500/15 border-amber-500/40',
                  purple: 'text-purple-400 bg-purple-500/15 border-purple-500/40',
                  orange: 'text-orange-400 bg-orange-500/15 border-orange-500/40',
                  emerald: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/40',
                  sky: 'text-sky-400 bg-sky-500/15 border-sky-500/40',
                };
                return (
                  <button
                    key={filter.id}
                    onClick={() => onToggleQuickFilter?.(filter.id)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                      isActive
                        ? colorMap[filter.color]
                        : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-zinc-800 mx-4" />

          {/* ---- Project Type Filters ---- */}
          <div className="px-4 py-3">
            <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Project Type</label>
            <div className="flex gap-1.5">
              {TYPE_FILTERS.map(tf => {
                const isActive = activeTypeFilters.includes(tf.id);
                const Icon = tf.icon;
                return (
                  <button
                    key={tf.id}
                    onClick={() => onToggleTypeFilter?.(tf.id)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all',
                      isActive
                        ? 'border-opacity-50 bg-opacity-15'
                        : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                    )}
                    style={isActive ? {
                      color: tf.color,
                      backgroundColor: tf.color + '18',
                      borderColor: tf.color + '60',
                    } : {}}
                  >
                    <Icon className="w-3 h-3" />
                    {tf.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-zinc-800 mx-4" />

          {/* ---- Shape / Feel Filters ---- */}
          <div className="px-4 py-3">
            <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">
              How does it feel?
            </label>
            <div className="grid grid-cols-5 gap-1.5">
              {SHAPE_FILTERS.map(shape => {
                const isActive = activeShapeFilters.includes(shape.id);
                return (
                  <button
                    key={shape.id}
                    onClick={() => onToggleShapeFilter?.(shape.id)}
                    title={shape.label}
                    className={cn(
                      'flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg border transition-all',
                      isActive
                        ? 'bg-violet-500/20 border-violet-500/50 text-violet-300'
                        : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                    )}
                  >
                    <span className={cn('text-base leading-none', isActive ? 'text-violet-300' : 'text-zinc-300')}>
                      {shape.symbol}
                    </span>
                    <span className="text-[9px] font-medium leading-none">{shape.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-zinc-800 mx-4" />

          {/* ---- Zone Filters with counts ---- */}
          <div className="px-4 py-3">
            <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Zones</label>
            <p className="text-[10px] text-zinc-600 mb-2.5 leading-relaxed">Tags you add when creating a project determine which zone it appears in</p>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(ZONES).map(([id, zone]) => {
                const count = zoneProjectCounts[id] || 0;
                const exampleKeywords = (zone.keywords || []).slice(0, 3).join(', ');
                return (
                  <div key={id} className="relative group/zone">
                    <button
                      onClick={() => onToggleZone(id)}
                      className={cn(
                        'flex flex-col gap-0.5 px-2.5 py-2 rounded-lg text-xs transition-all text-left w-full pr-7',
                        activeZones.includes(id)
                          ? `${zone.bgGlow} ${zone.textColor} border ${zone.borderColor}`
                          : 'bg-zinc-800/30 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 border border-transparent'
                      )}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />
                        <span className="truncate flex-1">{zone.shortLabel}</span>
                        <span className={cn(
                          'text-[10px] font-medium flex-shrink-0',
                          activeZones.includes(id) ? zone.textColor : 'text-zinc-600'
                        )}>
                          {count}
                        </span>
                      </div>
                      {exampleKeywords && (
                        <span className="text-[9px] text-zinc-600 truncate pl-4 leading-tight">{exampleKeywords}</span>
                      )}
                    </button>
                    {/* Zone home link — appears on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/zone/${id}`); }}
                      className="absolute top-1.5 right-1.5 p-0.5 rounded opacity-0 group-hover/zone:opacity-100 transition-opacity hover:bg-zinc-700/60"
                      title={`${zone.label} community`}
                    >
                      <ArrowUpRight className="w-3 h-3" style={{ color: zone.color }} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>


          <div className="h-px bg-zinc-800 mx-4" />


          {/* ---- Sort ---- */}
          <div className="px-4 py-3">
            <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Sort by</label>
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800/40 border border-zinc-700/50 rounded-lg text-sm text-zinc-300 hover:border-zinc-600 transition-colors"
              >
                <span className="flex items-center gap-2">
                  {(() => {
                    const current = SORT_OPTIONS.find(s => s.id === sortBy);
                    const Icon = current?.icon || Clock;
                    return (<><Icon className="w-3.5 h-3.5 text-zinc-500" />{current?.label || 'Newest First'}</>);
                  })()}
                </span>
                <ChevronDown className={cn('w-3.5 h-3.5 text-zinc-500 transition-transform', showSortDropdown && 'rotate-180')} />
              </button>
              {showSortDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg z-10 overflow-hidden">
                  {SORT_OPTIONS.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => { onSortChange?.(option.id); setShowSortDropdown(false); }}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                          sortBy === option.id ? 'text-purple-400 bg-purple-500/10' : 'text-zinc-300 hover:bg-zinc-700'
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {option.label}
                        {sortBy === option.id && <Check className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-zinc-800 mx-4" />

          {/* ---- Show Challenges Toggle ---- */}
          <div className="px-4 py-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-zinc-300 flex items-center gap-2">
                <Target className="w-3.5 h-3.5 text-amber-500" />
                Show Challenges
              </span>
              <button
                onClick={onToggleChallenges}
                className={cn(
                  'w-10 h-6 rounded-full transition-colors relative',
                  showChallenges ? 'bg-amber-500' : 'bg-zinc-700'
                )}
              >
                <span className={cn(
                  'absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm',
                  showChallenges ? 'translate-x-5' : 'translate-x-1'
                )} />
              </button>
            </label>
          </div>

          {/* ---- Show Use Echoes Toggle ---- */}
          {/* Echoes are opt-in: a faint copy of a project at the edge of each
              OTHER zone it is used in. Never drawn unless this is on. */}
          {onToggleEchoes && (
            <>
              <div className="h-px bg-zinc-800 mx-4" />
              <div className="px-4 py-3">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="min-w-0">
                    <span className="text-sm text-zinc-300 flex items-center gap-2">
                      <Layers className="w-3.5 h-3.5 text-fuchsia-400" />
                      Show Use echoes
                    </span>
                    <span className="block text-[10px] text-zinc-600 leading-snug mt-0.5 pl-[22px]">
                      {echoCount > 0
                        ? 'Ghost copies of projects in the other zones they are used in'
                        : 'No Uses in other zones yet'}
                    </span>
                  </span>
                  <button
                    onClick={onToggleEchoes}
                    role="switch"
                    aria-checked={showEchoes}
                    aria-label="Show Use echoes"
                    className={cn(
                      'w-10 h-6 rounded-full transition-colors relative flex-shrink-0',
                      showEchoes ? 'bg-fuchsia-500' : 'bg-zinc-700'
                    )}
                  >
                    <span className={cn(
                      'absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm',
                      showEchoes ? 'translate-x-5' : 'translate-x-1'
                    )} />
                  </button>
                </label>
              </div>
            </>
          )}
        </div>
      )}

      {/* ====== BOTTOM RIGHT  --  Zoom + Controls ====== */}
      {/* The zoom/reset/legend cluster is <MapChrome>, shared with the
          cohort map. It used to be written out here, and the cohort map
          had nothing at all; when the cohort map finally got controls,
          this markup was copied. Two copies of four buttons is exactly
          how the two maps drifted apart in the first place, so this
          renders the shared component instead of a duplicate.

          The view-mode toggle below stays here: cosmic-vs-grid is a
          main-map concept, and the cohort page has its own three-way
          grid/list/map switch in its header. */}
      <div className="absolute bottom-[60px] right-4 z-20 flex flex-col gap-2">
        <MapChrome
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onReset={onReset}
          onToggleLegend={onToggleLegend}
          showLegend={showLegend}
          className=""
        />

        <div className="flex bg-zinc-900/90 backdrop-blur-xl border border-zinc-800 rounded-xl overflow-hidden">
          <button
            onClick={() => onViewModeChange('cosmic')}
            className={cn('p-2.5 transition-colors', viewMode === 'cosmic' ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-zinc-800 text-zinc-400')}
            title="Cosmic view"
          >
            <MapIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('grid')}
            className={cn('p-2.5 transition-colors', viewMode === 'grid' ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-zinc-800 text-zinc-400')}
            title="Grid view"
          >
            <Grid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/*BOTTOM LEFT Create Button — hidden when SelectionTray is expanded */}
      {!isMultiSelectMode && !isTrayExpanded && (
        <div className="absolute bottom-[60px] left-4 z-50">
          <Button onClick={onCreateProject} icon={Plus} className="shadow-lg shadow-purple-500/20">
            New Project
          </Button>
        </div>
      )}

      {/* Zoom indicator */}
      <div className="absolute bottom-[60px] left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 bg-zinc-900/80 backdrop-blur rounded-full">
        <span className="text-xs text-zinc-400 tabular-nums">{Math.round(zoom * 100)}%</span>
      </div>

      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default MapControls;